import { AsyncPipe } from '@angular/common'
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox'
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  defer,
  map,
  startWith,
} from 'rxjs'

import {
  TRYGROUP_SEGMENTS,
  TrygroupPriorityListDepartment,
  TrygroupPriorityListFilter,
  TrygroupPriorityListUserCategory,
  TrygroupSegment,
  createDefaultPriorityListFilter,
} from '../../../services/trygroup-dashboard/trygroup-dashboard-priority-list.service'

@Component({
  selector: 'app-priority-list-filter-pane',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatAutocompleteModule,
  ],
  template: `
    <section class="filter-pane" aria-label="育成優先リストのフィルタ">
      <div class="filter-group">
        <span class="filter-label">セグメント</span>
        <div class="inline-options">
          @for (segment of segments; track segment) {
            <mat-checkbox
              [checked]="isSegmentSelected(segment)"
              [disabled]="disabled"
              (change)="onSegmentChange(segment, $event)"
            >
              {{ segment }}
            </mat-checkbox>
          }
        </div>
      </div>

      <div class="filter-group category-filter">
        <span class="filter-label">エリア</span>
        <div class="inline-options category-options">
          @for (category of userCategories; track category.id) {
            <mat-checkbox
              [checked]="isUserCategorySelected(category.id)"
              [disabled]="disabled || !category.active"
              (change)="onUserCategoryChange(category.id, $event)"
            >
              {{ category.name }}
            </mat-checkbox>
          }
        </div>
      </div>

      <div class="filter-group">
        <span class="filter-label">要指導</span>
        <mat-checkbox
          [checked]="filter.requiresGuidanceOnly"
          [disabled]="disabled"
          (change)="onRequiresGuidanceOnlyChange($event)"
        >
          要指導のみ
        </mat-checkbox>
      </div>

      <div class="filter-group department-filter">
        <span class="filter-label">部署</span>
        <input
          type="text"
          [formControl]="searchControl"
          [matAutocomplete]="departmentAutocomplete"
          aria-label="部署で絞り込み"
          placeholder="部署名で検索"
          (input)="onInputClear($event)"
        />
        <mat-autocomplete
          #departmentAutocomplete="matAutocomplete"
          [displayWith]="displayDepartmentName"
          (optionSelected)="onDepartmentChange($event.option.value)"
        >
          <mat-option value="">すべて</mat-option>
          @for (
            department of (filteredDepartments | async) ?? [];
            track department.id
          ) {
            <mat-option [value]="department.id">
              {{ department.name }}
            </mat-option>
          }
        </mat-autocomplete>
      </div>

      <div class="filter-group search-filter">
        <label>
          <span class="filter-label">検索</span>
          <input
            type="search"
            [(ngModel)]="searchKeywordInput"
            [disabled]="disabled"
            [attr.maxlength]="searchKeywordMaxLength"
            placeholder="氏名・メール (Enter で確定)"
            (keyup.enter)="onSearchKeywordSubmit()"
            (search)="onSearchKeywordSubmit()"
          />
        </label>
      </div>
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .filter-pane {
        display: grid;
        grid-template-columns:
          minmax(150px, 0.8fr) minmax(240px, 1.4fr)
          minmax(120px, 0.7fr) minmax(180px, 1fr)
          minmax(180px, 1fr);
        gap: 16px;
        padding: 16px;
        margin-bottom: 16px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .filter-group,
      label {
        display: grid;
        gap: 8px;
        align-content: start;
      }

      .filter-label {
        color: #475569;
        font-size: 12px;
        font-weight: 800;
      }

      .inline-options {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 12px;
      }

      .category-options {
        max-height: 92px;
        overflow: auto;
      }

      input {
        box-sizing: border-box;
        width: 100%;
        min-width: 0;
        height: 40px;
        padding: 0 12px;
        color: #0f172a;
        font: inherit;
        font-size: 13px;
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 6px;
      }

      input:focus {
        border-color: #2563eb;
        outline: 2px solid #bfdbfe;
        outline-offset: 1px;
      }

      @media (width < 1100px) {
        .filter-pane {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      @media (width < 720px) {
        .filter-pane {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class PriorityListFilterPaneComponent implements OnChanges {
  readonly segments = TRYGROUP_SEGMENTS
  /** backend MAX_SEARCH_KEYWORD_LENGTH と揃える。サーバラウンドトリップ前に UI 側で制限する。 */
  readonly searchKeywordMaxLength = 100
  readonly searchControl = new FormControl('', { nonNullable: true })

  @Input() filter: TrygroupPriorityListFilter =
    createDefaultPriorityListFilter()
  @Input() userCategories: TrygroupPriorityListUserCategory[] = []
  @Input() disabled = false

  @Output() filterChange = new EventEmitter<TrygroupPriorityListFilter>()

  /** 検索入力の一時 state。IME 変換中の即時 fetch を避けるため Enter / search で確定する。 */
  searchKeywordInput = this.filter.searchKeyword

  private readonly departmentsSubject = new BehaviorSubject<
    TrygroupPriorityListDepartment[]
  >([])

  readonly filteredDepartments: Observable<TrygroupPriorityListDepartment[]> =
    combineLatest([
      this.departmentsSubject,
      defer(() =>
        this.searchControl.valueChanges.pipe(
          startWith(this.searchControl.value),
        ),
      ),
    ]).pipe(
      map(([departments, searchValue]) =>
        this.filterDepartments(departments, searchValue),
      ),
    )

  readonly displayDepartmentName = (departmentId: string | null): string => {
    if (departmentId == null || departmentId === '') {
      return ''
    }

    return (
      this.departmentsSubject.value.find(
        (department) => department.id === departmentId,
      )?.name ?? ''
    )
  }

  @Input() set departments(value: TrygroupPriorityListDepartment[]) {
    this.departmentsSubject.next(value)
  }
  get departments(): TrygroupPriorityListDepartment[] {
    return this.departmentsSubject.value
  }

  get selectedDepartmentId(): string {
    if (this.filter.departmentId == null) {
      return ''
    }

    return this.filter.departmentId
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      this.searchKeywordInput = this.filter.searchKeyword
      const prev = changes['filter'].previousValue as
        | TrygroupPriorityListFilter
        | undefined
      if (prev?.departmentId !== this.filter.departmentId) {
        this.syncDepartmentSearchInput()
      }
    }

    if (changes['departments']) {
      this.syncDepartmentSearchInput()
    }

    if (changes['disabled']) {
      this.syncDepartmentControlDisabled()
    }
  }

  isSegmentSelected(segment: TrygroupSegment): boolean {
    return this.filter.segments.includes(segment)
  }

  isUserCategorySelected(userCategoryId: string): boolean {
    return this.filter.userCategoryIds.includes(userCategoryId)
  }

  onSegmentChange(segment: TrygroupSegment, event: MatCheckboxChange): void {
    const segments = event.checked
      ? [...this.filter.segments, segment]
      : this.filter.segments.filter((selected) => selected !== segment)

    this.emitFilter({ segments })
  }

  onUserCategoryChange(userCategoryId: string, event: MatCheckboxChange): void {
    const userCategoryIds = event.checked
      ? [...this.filter.userCategoryIds, userCategoryId]
      : this.filter.userCategoryIds.filter(
          (selected) => selected !== userCategoryId,
        )

    this.emitFilter({ userCategoryIds })
  }

  onRequiresGuidanceOnlyChange(event: MatCheckboxChange): void {
    this.emitFilter({ requiresGuidanceOnly: event.checked })
  }

  onDepartmentChange(departmentId: string): void {
    this.searchControl.setValue(departmentId, { emitEvent: false })
    this.emitFilter({
      departmentId: departmentId === '' ? null : departmentId,
    })
  }

  onInputClear(event?: Event): void {
    const inputValue =
      event?.target instanceof HTMLInputElement
        ? event.target.value
        : this.searchControl.value

    if (inputValue === '' && this.filter.departmentId != null) {
      this.onDepartmentChange('')
    }
  }

  onSearchKeywordSubmit(): void {
    if (this.searchKeywordInput === this.filter.searchKeyword) {
      return
    }
    this.emitFilter({ searchKeyword: this.searchKeywordInput })
  }

  private syncDepartmentSearchInput(): void {
    this.searchControl.setValue(this.selectedDepartmentId)
  }

  private syncDepartmentControlDisabled(): void {
    if (this.disabled) {
      this.searchControl.disable({ emitEvent: false })
      return
    }

    this.searchControl.enable({ emitEvent: false })
  }

  /** searchValue は選択済み部署IDまたはユーザー入力テキストのどちらかが入る */
  private filterDepartments(
    departments: TrygroupPriorityListDepartment[],
    searchValue: string,
  ): TrygroupPriorityListDepartment[] {
    const selectedDepartment = departments.find(
      (department) => department.id === searchValue,
    )
    const searchText = (selectedDepartment?.name ?? searchValue)
      .trim()
      .toLocaleLowerCase()

    return [...departments]
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(
        (department) =>
          searchText === '' ||
          department.name.toLocaleLowerCase().includes(searchText),
      )
  }

  private emitFilter(patch: Partial<TrygroupPriorityListFilter>): void {
    this.filterChange.emit({
      ...this.filter,
      ...patch,
    })
  }
}
